import { Fragment, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { colors } from "brand";
import { formatVenueTime } from "common-fe/venueTime";
import { formatPercentage } from "score-common";
import PageShell from "../components/PageShell";
import { fetchClassResults, resolveClassId } from "../api";

// #1641 (design §3.1/§6.3) — the QR-code target: `/results/:showId/:year/
// :classNumber`, a public, no-poll (one-shot) read of a class's ranked
// results via apps/score-api/src/handlers/scoreboard-public.js's
// getPublicClassResults. Chrome'd by PageShell (the same branded frame the
// rest of this app's public pages use), NOT ScoreboardFrame — this is a
// normal scrollable page a rider lands on after scanning a QR code, not the
// full-bleed TV board.
//
// #1854 — the QR now encodes a short, human-readable class NUMBER (e.g.
// "246") rather than the internal fvd: classId, so this page is
// resolve-then-fetch: showId+classNumber -> classId via score-api's new
// public resolveClassByNumber route (api.js's resolveClassId), THEN the
// unchanged classId-keyed fetchClassResults. Two round trips per QR scan is
// fine — this is a one-shot read, not the 5s poll. Both calls share one
// error-rendering path below: every 404 from either call (show_not_found,
// view_not_found, class_not_found) reads the same generic "not available"
// way, since view_not_found is the show's exposure gate, not proof the
// class doesn't exist.
//
// `:year` DECISION (card #1641's payload-contract note 6): the endpoint
// does no year check at all (unlike fetchScoreboard's :year, which 404s on
// mismatch) — score-api's getPublicClassResults route has no year param.
// Chosen here: treat it as FE-side/cosmetic ONLY. It is never read from
// useParams below and never sent to fetchClassResults — validating it
// FE-side would just be a second, redundant place to encode "what year is
// this show," and a wrong year in a shared/bookmarked QR link would 404 the
// whole page for no reason the payload itself needs. The QR code this app
// generates (QRCorner) always encodes the current real year, so this only
// matters for a hand-edited or stale URL, where cosmetic is the right
// failure mode (a slightly wrong year in the address bar, not a broken
// page).
//
// The payload's `resultsFinal` / `resultsRevealed` / `placing` vs `rank`
// rules (design §4.5) are the same ones the card's judge pinned on the
// score-api handler — this page must not re-derive them from `ofCount`
// alone (see the handler's own header comment for why that pair is
// ambiguous).
//
// #1647-shape fix (card #1641 round 2 judge): `resultsRevealed` means the
// WHOLE class is scored (every non-scratched ride CONFIRMED/FINAL), not
// "there are publishable rows": rows are published regardless of
// resultsRevealed, so the ordinary mid-class state is `resultsRevealed:
// false` WITH a non-empty `results` array (design §6.2 mode type 6,
// "current class standings"). `resultsRevealed` is therefore not the right
// input to ANY wording branch here: the empty state keys off `results`
// being empty (nothing published yet, whatever the reason), and the
// final-vs-standings choice keys off `resultsFinal` alone.
function statusBanner({ resultsFinal, results = [] }) {
  if (results.length === 0) {
    return { text: "No results have been confirmed for this class yet.", severity: "info" };
  }
  if (resultsFinal) {
    return { text: "Final class results", severity: "success" };
  }
  return { text: "Current class standings", severity: "info" };
}

// Card #1694 AC4 — percent formatting goes through score-common's
// formatPercentage (3 decimals, "single source of truth for percentage
// rendering across the FEs" per its own doc comment) rather than a
// per-component `.toFixed()`. formatPercentage already returns "—" for a
// non-numeric/null composite, so this wrapper only exists to keep the call
// sites below reading as "score text" rather than a bare formatter name.
function scoreText(composite) {
  return formatPercentage(composite);
}

// Round-2 judge (AC3) — the Place column's fallback used to be a bare
// `row.placing ?? row.rank`, which only guards against null/undefined:
// FVD's `0` "not yet placed" sentinel is neither, so `0 ?? row.rank`
// evaluated to the literal 0 and rendered it as if it were a real place.
// AC3 requires it render with NO place shown instead — this page's own
// established empty-value convention (competitorNumber/riderName/horseName
// all fall back to the same glyph a few lines below) is the em-dash, so a
// sentinel placing uses that too rather than a bare blank string or a
// hyphen the rest of the page doesn't use.
function placeText(row, resultsFinal) {
  if (!resultsFinal) return row.rank;
  if (row.placing === 0) return "—";
  return row.placing ?? row.rank;
}

// Card #1693 (Jaybill's decision, comment 2026-08-18: "option A, division
// headers") — splits the class's `division` field ("Open, Adult Amateur,
// Junior/Young Rider", #756/mapper.js DIVISION_FLAG_LABELS) into its
// individual tokens, in field order. A blank/single-token field means
// "single-division class" (AC2's common case), which the caller must
// render with no heading at all.
function divisionTokens(divisionField) {
  if (typeof divisionField !== "string") return [];
  return divisionField
    .split(",")
    .map((token) => token.trim())
    .filter(Boolean);
}

// #945's own collision rule, reused verbatim (see
// apps/scoreadmin-fe/src/pages/ClassPlacings.jsx buildPlacingGroups):
// riderStatus is FVD free text, not a closed enum, so "Open" / "open " /
// "OPEN" must key to the same division rather than splitting into three.
function normalizeDivisionKey(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

// Round-2 judge (AC3, sha pending) — a `division` field with a repeated
// token under normalization ("Open, open") used to build ONE bucket in
// `byKey` but then push that SAME bucket object into `groups` twice (once
// per surviving token), duplicating every row in that division under two
// headings with duplicate React keys. Dedupe by normalized key, keeping the
// FIRST token's original casing as the label — the same first-occurrence
// rule #945's buildPlacingGroups already uses.
function dedupeTokens(tokens) {
  const seen = new Set();
  const out = [];
  for (const token of tokens) {
    const key = normalizeDivisionKey(token);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(token);
  }
  return out;
}

// Round-2 judge (AC3) — FVD's `0` placing is its "not yet placed" sentinel
// (measured from prod, card comment 2026-08-17: class 119 entry 445, Open,
// composite 66.03, placing 0, a non-scratched scored ride), not a real 4th
// place. Within one division's rows (already placing-then-composite
// ordered by score-api's compareResultsByPlacingThenComposite), pulls the
// placing:0 rows out and appends them AFTER every placed row, each side
// keeping its own incoming relative order (a stable partition, not a
// re-sort by score or name — nothing here recomputes a placing, it only
// moves where an already-stored 0 renders). Deliberately scoped to exactly
// `placing === 0`: `placing: null` (never placed at all) already sorts
// last through compareResultsByPlacingThenComposite's own `!= null` check,
// so it needs no help here, and reaching further than the literal 0
// sentinel AC3 names would be scope creep this diff doesn't need.
function sortGroupRows(rows) {
  const placed = [];
  const unplaced = [];
  for (const row of rows) {
    if (row?.placing === 0) unplaced.push(row);
    else placed.push(row);
  }
  return unplaced.length > 0 ? [...placed, ...unplaced] : rows;
}

// Card #1693 AC1/AC2/AC3/AC5 — groups `results` under one bucket per
// division, in the order the class's OWN `division` field lists them
// (never alphabetized, never re-derived from placing or composite order).
// Each row's division comes from `riderStatus`, which score-api's
// getPublicClassResults already resolves via a real score-person join
// (scoreboard-public.js's enrichRideIdentity) — this function only matches
// that already-joined value against the division tokens, it does not infer
// anything from placing or name. A single-division (or division-less)
// class always returns exactly one group with `label: null` — the ONLY
// code path, so the caller's "no heading" rendering for that group is
// byte-identical to the pre-#1693 flat table (AC2), not a special case
// bolted on beside it. A row whose riderStatus matches none of the class's
// own division tokens (rare: e.g. an older ingested rider with no
// riderStatus at all) is never dropped — AC4 forbids suppressing a stored
// placing — it lands in a trailing group with no heading of its own,
// keeping it visible without guessing which division it belongs to. Every
// group's rows pass through sortGroupRows so AC3's "0 sentinel sorts after
// the placed riders" holds inside every group, single-division included.
export function groupResultsByDivision(divisionField, results) {
  const rows = Array.isArray(results) ? results : [];
  const tokens = dedupeTokens(divisionTokens(divisionField));
  if (tokens.length <= 1) {
    return [{ key: "all", label: null, rows: sortGroupRows(rows) }];
  }

  const byKey = new Map(
    tokens.map((label) => [normalizeDivisionKey(label), { key: normalizeDivisionKey(label), label, rows: [] }])
  );
  const unresolved = { key: "__unresolved__", label: null, rows: [] };
  for (const row of rows) {
    const key = normalizeDivisionKey(row?.riderStatus);
    const bucket = key && byKey.has(key) ? byKey.get(key) : unresolved;
    bucket.rows.push(row);
  }
  const groups = tokens
    .map((label) => byKey.get(normalizeDivisionKey(label)))
    .filter((group) => group.rows.length > 0);
  if (unresolved.rows.length > 0) groups.push(unresolved);
  for (const group of groups) group.rows = sortGroupRows(group.rows);
  return groups;
}

export default function ClassResultsPage() {
  const { showId, classNumber } = useParams();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // #1854 — resolve-then-fetch: the URL only ever carries the
      // human-readable classNumber, so classId is resolved fresh on every
      // load (including Refresh) rather than cached across renders — this
      // is a one-shot QR-scan read, not a poll, so the extra round trip
      // costs nothing and never risks reusing a stale classId.
      const resolved = await resolveClassId({ showId, classNumber });
      const result = await fetchClassResults({ showId, classId: resolved.classId });
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [showId, classNumber]);

  useEffect(() => {
    load();
  }, [load]);

  const banner = data ? statusBanner(data) : null;
  const classTitle = data?.className || "Class results";
  const classSubtitle = data?.classNumber != null ? `Class ${data.classNumber}` : null;

  return (
    <PageShell>
      <Box sx={{ width: "100%" }}>
        {loading && !data && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress data-testid="class-results-loading" />
          </Box>
        )}

        {error && !data && (
          <Alert severity="error" data-testid="class-results-error" sx={{ mb: 2 }}>
            {/* Every 404 (show_not_found, view_not_found, class_not_found)
                reads the same way here on purpose — #1641's payload-contract
                note 3: view_not_found is the show's exposure gate, not proof
                the class doesn't exist, so this must never claim
                non-existence from the error code alone. */}
            Results aren&apos;t available for this class right now.
          </Alert>
        )}

        {data && (
          <>
            <Typography variant="h5" sx={{ color: "text.primary", fontWeight: 700 }}>
              {classTitle}
            </Typography>
            {classSubtitle && (
              <Typography variant="body2" sx={{ color: "text.secondary", mb: data.judgesPositions?.length ? 0.5 : 2 }}>
                {classSubtitle}
              </Typography>
            )}

            {/* Card #1694 AC3 — judge names + booth letters. Omitted from
                the payload entirely (never an empty array) when the class
                has no judge assignments on file, so this renders nothing
                rather than an empty "Judges:" line. */}
            {data.judgesPositions?.length ? (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 2 }}
                data-testid="class-results-judges"
              >
                {data.judgesPositions
                  .map(({ position, judges }) => `${position}: ${(judges || []).join(", ")}`)
                  .join(" · ")}
              </Typography>
            ) : null}

            <Alert severity={banner.severity} sx={{ mb: 2 }} data-testid="class-results-banner">
              {banner.text}
            </Alert>

            {data.results.length > 0 && (
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small" data-testid="class-results-table">
                  <TableHead>
                    <TableRow>
                      {/* #1641 payload-contract note 2: never label `rank`
                          "place"/"placing" before resultsFinal — only the
                          FINAL header/value switches to the committed
                          placing. */}
                      <TableCell sx={{ color: "text.secondary" }}>
                        {data.resultsFinal ? "Place" : "Rank"}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>#</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>Rider</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>Horse</TableCell>
                      <TableCell sx={{ color: "text.secondary" }} align="right">
                        Score
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Card #1693 (option A, division headers) — a
                        single-division class (the common case, AC2) is one
                        group with `label: null`, so no heading row ever
                        renders here and the markup is identical to the
                        pre-#1693 flat table. */}
                    {groupResultsByDivision(data.division, data.results).map((group) => (
                      <Fragment key={group.key}>
                        {group.label ? (
                          <TableRow data-testid="class-results-division-heading">
                            <TableCell
                              colSpan={5}
                              sx={{ color: "text.primary", fontWeight: 700, pt: 2, borderBottom: "none" }}
                            >
                              {group.label}
                            </TableCell>
                          </TableRow>
                        ) : null}
                        {group.rows.map((row) => (
                          <TableRow key={row.rideId} data-testid="class-results-row">
                            <TableCell sx={{ color: "text.primary", fontWeight: 700 }}>
                              {placeText(row, data.resultsFinal)}
                            </TableCell>
                            <TableCell sx={{ color: "text.primary" }}>
                              {row.competitorNumber ?? "—"}
                            </TableCell>
                            <TableCell sx={{ color: "text.primary" }}>
                              {row.riderName ?? "—"}
                              {row.riderStatus ? (
                                <Chip
                                  label={row.riderStatus}
                                  size="small"
                                  variant="outlined"
                                  sx={{ ml: 1, color: "text.secondary" }}
                                />
                              ) : null}
                            </TableCell>
                            <TableCell sx={{ color: "text.primary" }}>
                              {row.horseName ?? "—"}
                            </TableCell>
                            <TableCell sx={{ color: "text.primary" }} align="right">
                              {row.termReason ? (
                                // Card #2104 AC2 — a terminated ride shows its
                                // specific DR123.3 outcome (Scratched/Excused/
                                // Eliminated/No Show) INSTEAD OF the percentage,
                                // never both. Same field + read pattern #2060
                                // put on the TV board's RankedList: rendered
                                // verbatim, never re-derived here.
                                <span data-testid="class-results-term-reason">{row.termReason}</span>
                              ) : (
                                scoreText(row.composite)
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {data.ofCount} of {data.entryCount} {data.entryCount === 1 ? "ride" : "rides"} shown
              {" · "}
              as of {formatVenueTime(data.serverTime, { timeZone: data.venueTimezone })} venue
              time
            </Typography>
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button
            onClick={load}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />
            }
            sx={{ color: colors.foxOrange }}
            data-testid="class-results-refresh"
          >
            Refresh
          </Button>
        </Box>
      </Box>
    </PageShell>
  );
}
